import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classes from '@/core/ErrorFallback.module.scss';
import { SeeqLogo2026 } from '@/core/SeeqLogoOrange.atom';
import { useRandomId } from '@/core/hooks/useRandomId.hook';
import { isSystemTest } from '@/core/utilities';
import { reload } from '@/utilities/reload.utilities';
import { Button, ButtonWithPopover, Icon } from '@seeqdev/qomponents';

interface ErrorFallbackProps {
  error: Error;
  /** Attempt to re-render the original content */
  retry: () => void;
}

export const ErrorFallback = ({ error, retry }: ErrorFallbackProps) => {
  const { t } = useTranslation();
  useEffect(() => {
    const stringError = error.toString();
    // Dynamic import errors are likely due to the page being stale after a new deployment has been made. If it
    // tries to fetch a module with a hash that no longer exists on the server, it will fail, and the only way to
    // know the new hash is to refresh the page.
    if (stringError.includes('Failed to fetch dynamically imported module')) {
      reload();
    }
  }, [error]);

  return (
    <div className={classes.container}>
      <div className={classes.iconContainer}>
        <SeeqLogo2026 />
      </div>
      <p className={clsx(classes.title, 'h4')}>{t('ERROR_FALLBACK.TITLE')}</p>
      <p>{t('ERROR_FALLBACK.BODY')}</p>
      <p>
        <code>{error.toString()}</code>
      </p>
      <p>{t('ERROR_FALLBACK.RENDER_OR_REFRESH')}</p>
      <Button
        size="sm"
        variant="theme"
        extraClassNames={classes.retryButton}
        onClick={retry}
        label={t('ERROR_FALLBACK.RETRY')}
      />
    </div>
  );
};

export const ErrorFallbackMinimal = ({ error, retry }: ErrorFallbackProps) => {
  const { t } = useTranslation();
  return (
    <div className={clsx(classes.container, 'tw:max-w-sq-400!')}>
      <h4>
        <Icon icon="fa-exclamation-triangle" type="danger" testId="show-error-fallback-popover" extraClassNames="mr5" />
        {t('ERROR_FALLBACK.TITLE')}
      </h4>
      <p>{t('ERROR_FALLBACK.BODY')}</p>
      <p>
        <code>{error.toString()}</code>
      </p>
      <Button
        size="sm"
        variant="theme"
        extraClassNames={classes.retryButton}
        onClick={retry}
        label={t('ERROR_FALLBACK.RETRY')}
      />
    </div>
  );
};

export const ErrorFallbackIcon = ({ error, retry }: ErrorFallbackProps) => {
  const popoverId = useRandomId();

  const popover = (
    <div id={popoverId} className="greyPopover">
      <ErrorFallback error={error} retry={retry} />
    </div>
  );

  return (
    <>
      <ButtonWithPopover
        isPortal
        trigger={
          <Icon
            icon="fa-exclamation-triangle"
            large={true}
            type="danger"
            testId="show-error-fallback-popover"
            extraClassNames="cursorPointer"
          />
        }>
        {popover}
      </ButtonWithPopover>
      {isSystemTest() ? <>{error.stack}</> : ''}
    </>
  );
};
